import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Pencil, Copy, Trash2, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { formatDate, formatDuration, formatTime } from '../../utils/formatters';
import { confirmDialog } from '../../utils/alerts';
import StatusBadge from '../../components/StatusBadge';

const TimeEntryList = () => {
  const { isStaff, user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    consultant: '',
    approval_status: '',
    client: '',
    project: '',
    date_from: '',
    date_to: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (isStaff) fetchConsultants();
    fetchClients();
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff]);

  const buildParams = () => {
    const params = new URLSearchParams();
    if (filters.consultant) params.append('consultant', filters.consultant);
    if (filters.approval_status) params.append('approval_status', filters.approval_status);
    if (filters.client) params.append('client', filters.client);
    if (filters.project) params.append('project', filters.project);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    return params;
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const params = buildParams();
      const response = await api.get(`/time-entries/?${params.toString()}`);
      setEntries(response.data.results || response.data);
    } catch (err) {
      toast.error('Erro ao carregar lançamentos.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = buildParams();
      const response = await api.get(`/time-entries/export/?${params.toString()}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `lancamentos_${dateStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exportação concluída!');
    } catch (err) {
      toast.error('Erro ao exportar lançamentos.');
    } finally {
      setExporting(false);
    }
  };

  const fetchConsultants = async () => {
    try {
      const response = await api.get('/users/?role=consultor');
      setConsultants(response.data.results || response.data);
    } catch (err) {
      console.error('Erro ao carregar consultores', err);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients/');
      setClients(response.data.results || response.data);
    } catch (err) {
      console.error('Erro ao carregar clientes', err);
    }
  };

  const fetchProjects = async (clientId) => {
    try {
      const url = clientId ? `/projects/?client=${clientId}` : '/projects/';
      const response = await api.get(url);
      setProjects(response.data.results || response.data);
    } catch (err) {
      console.error('Erro ao carregar projetos', err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'client') {
      setFilters((prev) => ({ ...prev, client: value, project: '' }));
      fetchProjects(value);
      return;
    }
    setFilters({ ...filters, [name]: value });
  };

  const canEdit = (entry) => {
    if (isStaff) return true;
    return entry.consultant === user?.id && entry.approval_status === 'pending';
  };

  const handleDelete = async (entry) => {
    const confirmed = await confirmDialog({
      title: 'Excluir lançamento',
      text: `Tem certeza que deseja excluir o lançamento "${entry.title}" de ${formatDate(entry.entry_date)}?`,
      confirmText: 'Excluir',
    });
    if (!confirmed) return;
    try {
      await api.delete(`/time-entries/${entry.id}/`);
      toast.success('Lançamento removido com sucesso!');
      fetchEntries();
    } catch (err) {
      toast.error('Erro ao remover lançamento.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex flex-wrap gap-3">
          {isStaff && (
            <select
              name="consultant"
              value={filters.consultant}
              onChange={handleFilterChange}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="">Todos os consultores</option>
              {consultants.map((c) => (
                <option key={c.id} value={c.id}>{c.first_name || c.username}</option>
              ))}
            </select>
          )}
          <select
            name="approval_status"
            value={filters.approval_status}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">Todos os status</option>
            <option value="pending">Pendente</option>
            <option value="approved">Aprovado</option>
            <option value="rejected">Recusado</option>
          </select>
          <select
            name="client"
            value={filters.client}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">Todos os clientes</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            name="project"
            value={filters.project}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">Todos os projetos</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <input
            type="date"
            name="date_from"
            value={filters.date_from}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            title="Data de"
          />
          <input
            type="date"
            name="date_to"
            value={filters.date_to}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            title="Data até"
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium rounded-lg transition-colors disabled:opacity-70"
          >
            <Download size={16} />
            {exporting ? 'Exportando...' : 'Exportar XLSX'}
          </button>
          <Link
            to="/time-entries/new"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={16} />
            Novo Lançamento
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {isStaff && (
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Consultor</th>
                )}
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Data</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Período</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cliente/Projeto</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Título</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Horas</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={isStaff ? 8 : 7} className="px-6 py-12 text-center text-gray-400 text-sm">
                    Nenhum lançamento encontrado.
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50 transition-colors">
                    {isStaff && (
                      <td className="px-6 py-4 text-gray-900">{entry.consultant_name}</td>
                    )}
                    <td className="px-6 py-4 text-gray-600">{formatDate(entry.entry_date)}</td>
                    <td className="px-6 py-4 text-gray-600">
                      {entry.periods && entry.periods.length > 0 ? (
                        entry.periods.map((p, i) => (
                          <div key={i} className="text-xs whitespace-nowrap">
                            {formatTime(p.start_time)} - {formatTime(p.end_time)}
                          </div>
                        ))
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      <div className="font-medium text-gray-900">{entry.client_name}</div>
                      <div className="text-xs text-gray-500">{entry.project_name}</div>
                    </td>
                    <td className="px-6 py-4 text-gray-900">{entry.title}</td>
                    <td className="px-6 py-4 text-gray-600 font-medium">{formatDuration(entry.total_hours)}</td>
                    <td className="px-6 py-4">
                      <StatusBadge status={entry.approval_status} type="approval" />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        {canEdit(entry) && (
                          <button
                            onClick={() => navigate(`/time-entries/${entry.id}/edit`)}
                            className="text-blue-600 hover:text-blue-700"
                            title="Editar"
                          >
                            <Pencil size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/time-entries/${entry.id}/copy`)}
                          className="text-gray-500 hover:text-gray-700"
                          title="Copiar"
                        >
                          <Copy size={16} />
                        </button>
                        {canEdit(entry) && (
                          <button
                            onClick={() => handleDelete(entry)}
                            className="text-red-500 hover:text-red-700"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TimeEntryList;
